import type { ReactNode } from 'react'
import type {
  Correction,
  Evidence,
  EvidenceId,
  Ledger,
  LedgerItem,
  Party,
  Suppression,
  Transition,
  VerificationRecord,
} from '@cordelia/core'
import { Button } from '../Button/index.js'
import type { CardLinks } from '../Card/index.js'
import { longDate } from '../dates.js'
import { partyName } from '../partyName.js'
import { Sheet } from '../Sheet/index.js'
import styles from './Trace.module.scss'

/**
 * Trace — the inspectable chain behind one card (M3.5), in the Sheet: a side
 * sheet from the md breakpoint, a bottom sheet below it, focus trapped,
 * Escape and a close button, focus back on the card's Trace button.
 *
 * The seven sections follow the Constitution (*Trace and repair*) in order:
 * evidence; why it exists; why it is open, uncertain or stale; lineage;
 * suppressions and corrections; signals, which are not communication; why
 * now. Everything is words. Every quotation is exact and attributed with an
 * absolute timestamp in the source's own offset. Nothing here can be edited;
 * the correction sheet (M4.1) opens from here when it lands. Only this item's
 * evidence appears: Trace is not a thread view and never lists other messages.
 */
export type TraceProps = {
  readonly ledger: Ledger
  readonly item: LedgerItem
  /** The run's cutoff, for "as of". */
  readonly cutoff: string
  readonly links?: CardLinks | undefined
  /** Lets the card mark itself expanded while the sheet is open. */
  readonly onOpenChange?: ((open: boolean) => void) | undefined
}

export function Trace({ ledger, item, cutoff, links, onOpenChange }: TraceProps) {
  return (
    <Sheet
      title="Trace"
      description={item.proposition}
      trigger={
        <Button variant="quiet" size="sm">
          Trace
        </Button>
      }
      onOpenChange={onOpenChange}
    >
      <TraceBody ledger={ledger} item={item} cutoff={cutoff} links={links} />
    </Sheet>
  )
}

/** The words for verification's closed answers. */
const BOUND: Readonly<Record<VerificationRecord['answers']['bound'], string>> = {
  speaker: 'the speaker',
  speaker_org: "the speaker's organization",
  recipient: 'the recipient',
  third_party: 'a third party',
  nobody: 'nobody',
}
const ACCEPTED: Readonly<Record<VerificationRecord['answers']['accepted'], string>> = {
  self_bound: 'self-bound',
  accepted_in_span: 'accepted in the span',
  not_accepted: 'not accepted',
  not_applicable: 'not applicable',
}
const SCOPE_CLAIMED: Readonly<Record<VerificationRecord['answers']['scope_claimed'], string>> = {
  proposition_in_span: 'stays inside the span',
  proposition_adds_detail: 'adds detail the span does not state',
  proposition_contradicts_span: 'contradicts the span',
}
const VERDICT: Readonly<Record<VerificationRecord['verdict'], string>> = {
  verified: 'Verified',
  disputed: 'Partly supported',
  rejected: 'Rejected',
}

/** What a person's suppression reason says, in words. */
const USER_SUPPRESSION: Readonly<Record<string, string>> = {
  'user:dismiss': 'Dismissed by you',
  'user:irrelevant_or_private': 'Marked irrelevant or private by you',
  'user:not_a_commitment': 'Marked not a commitment by you',
  'user:draft_abandoned': 'Marked as a draft abandoned on purpose by you',
}

const words = (s: string): string => s.replace(/_/g, ' ')

type Ctx = {
  readonly ledger: Ledger
  readonly cutoff: string
  readonly evidenceById: ReadonlyMap<EvidenceId, Evidence>
  readonly links: CardLinks | undefined
  readonly name: (party: Party | 'system') => string
}

function TraceBody({
  ledger,
  item,
  cutoff,
  links,
}: {
  ledger: Ledger
  item: LedgerItem
  cutoff: string
  links: CardLinks | undefined
}) {
  const ctx: Ctx = {
    ledger,
    cutoff,
    evidenceById: new Map(ledger.evidence.map((e) => [e.id, e])),
    links,
    name: (party) => (party === 'system' ? 'the system' : partyName(party, ledger.identity)),
  }
  const claim = ledger.claims.find((c) => c.id === item.claim_id)
  const group = ledger.lineage.find((g) => g.claim_ids.includes(item.claim_id))
  const groupClaims = new Set(group?.claim_ids ?? [item.claim_id])
  const evidence = (claim?.evidence_ids ?? [])
    .map((id) => ctx.evidenceById.get(id))
    .filter((e): e is Evidence => e !== undefined)
  const evidenceIds = new Set(evidence.map((e) => e.id))
  const verification = ledger.verifications.find((v) => v.claim_id === item.claim_id)
  const roles = ledger.identity.roles.filter(
    (r) => r.person_id === item.made_by && r.organization_id === item.owed_by,
  )
  const transitions = [...(group?.transitions ?? [])].sort(
    (a, b) => transitionTime(ctx, a) - transitionTime(ctx, b),
  )
  const suppressions = ledger.suppressions.filter(
    (s) =>
      s.item_id === item.id ||
      (s.claim_id !== null && groupClaims.has(s.claim_id)) ||
      (s.source_id !== null && evidence.some((e) => e.source_id === s.source_id)),
  )
  const corrections = ledger.corrections.filter((c) =>
    c.affects.some(
      (id) =>
        id === item.id ||
        groupClaims.has(id as (typeof item)['claim_id']) ||
        evidenceIds.has(id as EvidenceId),
    ),
  )
  const drafts = ledger.draft_signals.filter((d) => d.item_id === item.id)
  const replies = ledger.reply_owed_signals.filter((r) => r.item_id === item.id)

  return (
    <div className={styles.root}>
      <Section title="Evidence">
        {evidence.length > 0 ? (
          <ul className={styles.list}>
            {evidence.map((ev) => (
              <li key={ev.id}>
                <EvidenceBlock ctx={ctx} evidence={ev} />
              </li>
            ))}
          </ul>
        ) : (
          <p className={styles.dim}>No evidence is recorded on this card.</p>
        )}
      </Section>

      <Section title="Why it exists">
        {verification ? (
          <>
            <p className={styles.text}>
              {VERDICT[verification.verdict]}: {verification.reason}
            </p>
            <ul className={styles.answers}>
              <li>Said it: {verification.answers.said}</li>
              <li>Bound: {BOUND[verification.answers.bound]}</li>
              <li>Accepted: {ACCEPTED[verification.answers.accepted]}</li>
              <li>Force used: {verification.answers.modality}</li>
              <li>Proposition: {SCOPE_CLAIMED[verification.answers.scope_claimed]}</li>
            </ul>
          </>
        ) : (
          <p className={styles.dim}>No verification record.</p>
        )}
        <p className={styles.dim}>
          Who said it: {ctx.name(item.made_by)} · who owes it: {ctx.name(item.owed_by)} · to whom:{' '}
          {ctx.name(item.owed_to)} · positioned to act:{' '}
          {item.responsible_actor === 'unresolved'
            ? 'not established'
            : ctx.name(item.responsible_actor)}
        </p>
        {roles.map((role) => {
          const signatures = role.evidence_ids
            .map((id) => ctx.evidenceById.get(id))
            .filter((e): e is Evidence => e !== undefined)
          const n = role.evidence_ids.length
          return (
            <div key={`${role.person_id}-${role.organization_id}`} className={styles.section}>
              <p className={styles.text}>
                You signed as {role.title} at {ctx.name(role.organization_id)} in {n}{' '}
                {n === 1 ? 'message' : 'messages'}.
              </p>
              {signatures.map((ev) => (
                <EvidenceBlock key={ev.id} ctx={ctx} evidence={ev} />
              ))}
            </div>
          )
        })}
      </Section>

      <Section title={`Why it is ${item.openness_state}`}>
        <ul className={styles.list}>
          {whyOpen(ctx, item).map((line, i) => (
            <li key={i}>{line}</li>
          ))}
        </ul>
      </Section>

      <Section title="Lineage">
        {transitions.length > 0 ? (
          <ul className={styles.list}>
            {transitions.map((t, i) => (
              <li key={i}>
                <TransitionLine ctx={ctx} transition={t} />
              </li>
            ))}
          </ul>
        ) : (
          <p className={styles.dim}>
            No later message modified, delegated, fulfilled or cancelled it.
          </p>
        )}
      </Section>

      <Section title="Suppressions and corrections">
        {suppressions.length === 0 && corrections.length === 0 && item.diagnostics.length === 0 ? (
          <p className={styles.dim}>None. Nothing about this card has been hidden or changed.</p>
        ) : (
          <ul className={styles.list}>
            {item.diagnostics.map((d, i) => (
              <li key={`d${i}`}>
                {d.kind === 'role_unresolved'
                  ? `Role not established: ${d.reason}.`
                  : d.kind === 'weakened'
                    ? `Weakened ${words(d.field)}: ${d.reason}.`
                    : `${words(d.kind)}.`}
              </li>
            ))}
            {suppressions.map((s) => (
              <li key={s.id}>{suppressionLine(s)}</li>
            ))}
            {corrections.map((c) => (
              <li key={c.id}>{correctionLine(c, ctx)}</li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Signals — not communication">
        {drafts.length === 0 && replies.length === 0 ? (
          <p className={styles.dim}>
            None. Every line above comes from a message that was sent or received.
          </p>
        ) : (
          <ul className={styles.list}>
            {drafts.map((d) => {
              const href = ctx.links?.draft?.(d) ?? null
              return (
                <li key={d.id}>
                  <p className={styles.signal}>
                    <span className={styles.signalLabel}>Not communication · unsent draft</span>
                    An unsent draft to {ctx.name(d.recipient)}, started {longDate(d.created_at)}. It
                    proves nothing was received.
                    {href ? (
                      <>
                        {' '}
                        <a href={href} target="_blank" rel="noopener">
                          Continue draft
                        </a>
                      </>
                    ) : null}
                  </p>
                </li>
              )
            })}
            {replies.map((r) => {
              // The ask is communication and is quoted as evidence, beside the
              // signal and never inside it.
              const ev = ctx.evidenceById.get(r.evidence_id)
              return (
                <li key={r.id} className={styles.section}>
                  <p className={styles.signal}>
                    <span className={styles.signalLabel}>Not communication · reply owed</span>
                    {ctx.name(r.counterparty)} asked on {longDate(r.asked_at)} and has not heard
                    back. A reply may be expected; this is not a commitment.
                  </p>
                  {ev ? <EvidenceBlock ctx={ctx} evidence={ev} /> : null}
                </li>
              )
            })}
          </ul>
        )}
      </Section>

      <Section title="Why now">
        {item.attention_reasons.length > 0 ? (
          <ul className={styles.list}>
            {item.attention_reasons.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
        ) : (
          <p className={styles.dim}>Nothing marks it out; it sits in its section's order.</p>
        )}
      </Section>
    </div>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className={styles.section}>
      <h3 className={styles.heading}>{title}</h3>
      {children}
    </section>
  )
}

function InlineQuote({ text }: { text: string }) {
  return <q className={styles.inlineQuote}>{text}</q>
}

/** A whole quotation with its provenance: speaker, audience, state, absolute time, the source. */
function EvidenceBlock({ ctx, evidence }: { ctx: Ctx; evidence: Evidence }) {
  const href = ctx.links?.source?.(evidence) ?? null
  const audience = evidence.audience.map((a) => ctx.name(a)).join(', ') || 'unknown'
  return (
    <figure className={styles.evidence}>
      <blockquote className={styles.quote}>“{evidence.exact_quote}”</blockquote>
      <figcaption className={styles.attribution}>
        {ctx.name(evidence.speaker)} → {audience} · {evidence.source_state} ·{' '}
        {longDate(evidence.timestamp)}
        {href ? (
          <>
            {' · '}
            <a href={href} target="_blank" rel="noopener">
              Open in Gmail
            </a>
          </>
        ) : null}
      </figcaption>
    </figure>
  )
}

function whyOpen(ctx: Ctx, item: LedgerItem): ReactNode[] {
  const lines: ReactNode[] = []
  for (const oe of item.openness_evidence) {
    const ev = ctx.evidenceById.get(oe.evidence_id)
    switch (oe.kind) {
      case 'counterparty_chase':
        if (ev)
          lines.push(
            <>
              {ctx.name(ev.speaker)} followed up on {longDate(ev.timestamp)}:{' '}
              <InlineQuote text={ev.exact_quote} />
            </>,
          )
        break
      case 'explicit_pending':
        if (ev)
          lines.push(
            <>
              Marked as still pending on {longDate(ev.timestamp)}:{' '}
              <InlineQuote text={ev.exact_quote} />
            </>,
          )
        break
      case 'reaffirmed':
        if (ev)
          lines.push(
            <>
              Reaffirmed on {longDate(ev.timestamp)}: <InlineQuote text={ev.exact_quote} />
            </>,
          )
        break
      case 'future_deadline':
        lines.push(`Its stated timing has not passed as of ${ctx.cutoff}.`)
        break
      case 'unexpired_horizon':
        lines.push('No timing was stated; fewer than 14 days have passed since it was made.')
        break
      case 'user_reference':
        lines.push('You referred to it later as still pending.')
        break
      case 'user_observation':
        lines.push('You told us.')
        break
    }
  }
  if (item.openness_state === 'uncertain')
    lines.push(
      `Nothing since ${longDate(item.last_evidence_at)}. We don't know whether it happened.`,
    )
  if (item.openness_state === 'stale')
    lines.push(`Nothing since ${longDate(item.last_evidence_at)}, well past the horizon it stated.`)
  if (item.openness_state === 'resolved') lines.push(`Resolved: ${item.status}.`)
  if (lines.length === 0) lines.push('No later message bears on it.')
  return lines
}

function transitionTime(ctx: Ctx, t: Transition): number {
  const times = t.evidence_ids
    .map((id) => ctx.evidenceById.get(id))
    .filter((e): e is Evidence => e !== undefined)
    .map((e) => Date.parse(e.timestamp))
  return times.length > 0 ? Math.min(...times) : Number.POSITIVE_INFINITY
}

/** One transition: what happened, when, in whose words, and which claim it refers to. */
function TransitionLine({ ctx, transition }: { ctx: Ctx; transition: Transition }) {
  const ev = transition.evidence_ids
    .map((id) => ctx.evidenceById.get(id))
    .find((e): e is Evidence => e !== undefined)
  const prior = ctx.ledger.claims.find((c) => c.id === transition.from_claim_id)
  return (
    <>
      <span className={styles.text}>
        {words(transition.type)}
        {ev ? ` on ${longDate(ev.timestamp)}` : ''}
        {ev ? (
          <>
            , {ctx.name(ev.speaker)}: <InlineQuote text={ev.exact_quote} />
          </>
        ) : null}
      </span>
      {prior ? (
        <p className={styles.dim}>
          Of: <InlineQuote text={prior.proposition} />
        </p>
      ) : null}
    </>
  )
}

function suppressionLine(s: Suppression): string {
  const what =
    s.actor === 'user'
      ? (USER_SUPPRESSION[s.reason] ?? `Suppressed by you (${words(s.reason)})`)
      : `Suppressed by ${words(s.actor.replace('policy:', 'policy '))} (${words(s.reason)})`
  const until =
    s.duration === 'until_new_evidence'
      ? 'until a later message bears on it'
      : 'until you say otherwise'
  return `${what} on ${longDate(s.time)}; ${s.reversible ? 'reversible' : 'not reversible'}, ${until}.`
}

/** Whether a value names someone the identity graph knows. */
function isParty(value: string, ctx: Ctx): value is Party {
  const { persons, organizations } = ctx.ledger.identity
  return persons.some((p) => p.id === value) || organizations.some((o) => o.id === value)
}

/**
 * A corrected value in words. A party is its name from the identity graph and
 * never an id; a suppression's `{ reason }` is the reason; anything without a
 * reading of its own falls back to its JSON rather than to nothing, so a value
 * is never silently dropped from the record.
 */
function correctionValue(value: unknown, ctx: Ctx): string {
  if (typeof value === 'string') {
    if (value === 'unresolved') return 'not established'
    // A party is one the graph knows, not one that looks like an id: asking
    // the graph never mistakes an ordinary word for a person.
    if (isParty(value, ctx)) return ctx.name(value)
    return words(value)
  }
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  if (value !== null && typeof value === 'object') {
    const reason = (value as { reason?: unknown }).reason
    if (typeof reason === 'string') return words(reason.replace(/^user:/, ''))
  }
  return JSON.stringify(value)
}

/** Whether a correction replaced anything. A first observation replaces nothing. */
const hasPrior = (c: Correction): boolean =>
  c.previous_value !== null && c.previous_value !== undefined && c.previous_value !== ''

/**
 * One correction, and what it replaced. The Spec's *Corrections and Trace*
 * promises the prior interpretation stays inspectable, so where a correction
 * carries the value it replaced (M4.2), Trace says it: the correction, the
 * previous reading and the current one are all on screen at once. A
 * correction that replaced nothing — the first observation about an item, or
 * a suppression, which hides a card rather than contesting a value — gets no
 * line rather than an empty one.
 */
function correctionLine(c: Correction, ctx: Ctx): string {
  const value = correctionValue(c.corrected_value, ctx)
  const made = `You corrected ${words(c.contested_field)} to ${value} on ${longDate(c.time)} (${words(c.reason)}).`
  return hasPrior(c)
    ? `${made} Previously believed: ${correctionValue(c.previous_value, ctx)}. The reading before it stays on record.`
    : `${made} The reading before it stays on record.`
}
